import React, { useState, useEffect } from 'react';
import { Check, ArrowRight, Shield, Zap, Award, Send, Users, Briefcase, Plane, Coins, Mail, Phone } from 'lucide-react';
import Logo from '../components/Logo';
import Footer from '../components/Footer';
import ThemeToggle from '../components/ThemeToggle';
import WalletMenu from '../components/WalletMenu';
import NavigationMenu from '../components/NavigationMenu';
import { useAuth } from '../context/AuthContext';
import { supabase } from '../lib/supabase';

interface MembershipTier {
  id: string;
  name: string;
  price?: number;
  priceDisplay?: string;
  description: string;
  features: string[];
  isPopular?: boolean;
  lifetime?: string;
  category: string;
}

const membershipTiers: MembershipTier[] = [
  {
    id: 'wine-caviar-flowers',
    name: 'Wine, Caviar & Flowers',
    description: 'For wine merchants, restaurants, and florists',
    category: 'Standard',
    features: ['One-time registration', 'No tiered options', 'Visibility for all employees', 'Basic listing']
  },
  {
    id: 'luxury-auto-rental',
    name: 'Luxury Car Rental',
    description: 'Exclusive visibility and CRM ad pushing',
    category: 'Premium',
    features: ['General: Basic visibility', 'Priority: Preferred placement', 'Elite: Exclusive visibility', 'CRM ad pushing for maximum reach'],
    isPopular: true
  },
  {
    id: 'yachting-sailing',
    name: 'Yachting & Sailing',
    description: 'Premium placement & branding',
    category: 'Premium',
    features: ['Basic: Listing for yachts', 'Pro: Premium tag & higher placement', 'Premium: Prioritized CRM placement', 'Exclusive branding']
  },
  {
    id: 'events',
    name: 'Events',
    description: 'VIP packages and increased visibility',
    category: 'Priority',
    features: ['Providers for events such as table reservations', 'VIP packages depending on subscription', 'Increased visibility', 'CRM ad pushing']
  },
  {
    id: 'concierge',
    name: 'Concierge',
    description: 'Elite visibility and prioritization',
    category: 'Priority',
    features: ['Depending on service level', 'Elite includes direct CRM visibility', 'Prioritization by staff', 'Preferred placement']
  },
  {
    id: 'airport-services',
    name: 'Airport Services',
    description: 'Priority listings for booking requests',
    category: 'Basic',
    features: ['Priority offers with ad pushing', 'Preferred listings for booking requests', 'Increased visibility', 'Basic support']
  },
  {
    id: 'adventures-sports',
    name: 'Adventures & Sports',
    description: 'Elite premium position and CRM priority',
    category: 'Priority',
    features: ['For providers like safaris, water sports, trekking', 'Elite offers premium positioning', 'CRM priority', 'Preferred placement']
  },
  {
    id: 'helicopter-rundfluge',
    name: 'Helicopter Tours',
    description: 'Tiered visibility and branding',
    category: 'General',
    features: ['Tour providers receive tiered visibility', 'Depending on subscription', 'Elite includes CRM ad pushing', 'Branding options']
  }
];

export default function Partners() {
  const { user } = useAuth();
  const [formData, setFormData] = useState({
    companyName: '',
    contactName: '',
    email: '',
    phone: '',
    website: '',
    businessType: '',
    referralCode: '',
    message: '',
    selectedTier: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [referralDiscount, setReferralDiscount] = useState(false);

  useEffect(() => {
    // Pre-fill form with user data if available
    if (user) {
      setFormData(prev => ({
        ...prev,
        email: user.email || '',
        contactName: user.name || ''
      }));
    }
  }, [user]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Check if referral code is valid
    if (name === 'referralCode' && value.trim()) {
      // In a real app, you would validate this against a database
      // For demo purposes, we'll accept any code
      if (value.length > 3) {
        setReferralDiscount(true);
      } else {
        setReferralDiscount(false);
      }
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // In a real application, you would send this data to your backend
      const { data, error } = await supabase
        .from('partner_applications')
        .insert([{
          company_name: formData.companyName,
          contact_name: formData.contactName,
          email: formData.email,
          phone: formData.phone,
          website: formData.website,
          business_type: formData.businessType,
          referral_code: formData.referralCode,
          message: formData.message,
          user_id: user?.id
        }]);
      
      if (error) throw error;
      
      // Send email to partners@privatecharterx.com
      // This would be handled by a server function in a real application
      console.log('Sending email to partners@privatecharterx.com', {
        subject: `New Partner Application: ${formData.companyName}`,
        body: `
          New partner application received:
          
          Company: ${formData.companyName}
          Contact: ${formData.contactName}
          Email: ${formData.email}
          Phone: ${formData.phone}
          Website: ${formData.website}
          Business Type: ${formData.businessType}
          Selected Tier: ${formData.selectedTier}
          Referral Code: ${formData.referralCode}
          
          Message:
          ${formData.message}
        `
      });
      
      setIsSubmitted(true);
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectTier = (tier: MembershipTier) => {
    setFormData(prev => ({
      ...prev,
      selectedTier: tier.id
    }));
    
    // Scroll to application form
    document.getElementById('application-form')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen flex flex-col">
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-gradient-to-b from-gray-50 to-white border-b border-gray-100">
        <div className="max-w-8xl mx-auto">
          <div className="flex items-center justify-between py-4 px-6">
            <div className="flex items-center gap-8">
              <Logo />
              <NavigationMenu />
            </div>
            <div className="flex items-center gap-4">
              <WalletMenu onShowDashboard={() => {}} />
              <ThemeToggle />
            </div>
          </div>
        </div>
      </header>

      <main className="flex-1 pt-[88px]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          {/* Hero Section */}
          <div className="text-center mb-16">
            <h1 className="text-4xl md:text-5xl font-bold mb-6">Partner With Us</h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Join our global network of premium service providers and expand your business reach. Become a PrivateCharterX partner and access our exclusive platform and clientele.
            </p>
          </div>

          {/* Benefits Section */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 mb-16">
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <Shield size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Global Exposure</h3>
              <p className="text-base text-gray-600">
                Gain access to our worldwide network of high-net-worth clients and corporate accounts seeking premium services.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Listing on our global platform</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Featured in marketing campaigns</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <Zap size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">CRM Ad-Pushing</h3>
              <p className="text-base text-gray-600">
                Our advanced CRM system promotes your services directly to relevant clients, increasing your visibility and booking rates.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Targeted promotion to ideal clients</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Priority placement in search results</span>
                </li>
              </ul>
            </div>
            
            <div className="bg-gray-50 p-8 rounded-2xl">
              <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center mb-4">
                <Award size={24} className="text-white" />
              </div>
              <h3 className="text-xl font-bold mb-2">Premium Positioning</h3>
              <p className="text-base text-gray-600">
                Associate your brand with our luxury platform and benefit from our reputation for excellence in premium services.
              </p>
              <ul className="mt-4 space-y-2">
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Luxury brand association</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                  <span className="text-gray-600">Access to exclusive events</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Referral Program */}
          <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-8 rounded-2xl mb-16">
            <div className="max-w-3xl mx-auto">
              <h2 className="text-2xl font-bold mb-4 text-center">Partner Referral Program</h2>
              <p className="text-gray-700 mb-6 text-center">
                Refer another business to join our partner network and receive a 10% discount on your membership fee. Simply have them enter your company name or email as the referral code when they apply.
              </p>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-start gap-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                    <Coins size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold text-lg mb-1">How It Works</h3>
                    <ol className="space-y-2 text-gray-600">
                      <li className="flex items-start gap-2">
                        <span className="font-bold">1.</span>
                        <span>Share your company name or email with potential partners</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">2.</span>
                        <span>They enter it as a referral code when applying</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">3.</span>
                        <span>Once they're approved, you receive a 10% discount on your next renewal</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="font-bold">4.</span>
                        <span>They also receive a 10% discount on their first year</span>
                      </li>
                    </ol>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Membership Tiers */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Membership Tiers</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              {membershipTiers.map((tier) => (
                <div 
                  key={tier.id}
                  className={`bg-white rounded-2xl border ${
                    tier.isPopular ? 'border-blue-500 shadow-xl' : 'border-gray-200'
                  } p-6 relative`}
                >
                  {tier.isPopular && (
                    <div className="absolute -top-4 left-1/2 -translate-x-1/2 bg-blue-500 text-white px-4 py-1 rounded-full text-sm font-medium">
                      Most Popular
                    </div>
                  )}
                  <div className="text-center mb-6">
                    <h3 className="text-xl font-bold mb-2">{tier.name}</h3>
                    <p className="text-gray-600 mb-4">{tier.description}</p>
                    <div className="text-2xl font-bold">{tier.priceDisplay}</div>
                    {tier.lifetime && (
                      <div className="text-sm text-gray-500">Lifetime: {tier.lifetime}</div>
                    )}
                  </div>

                  <ul className="space-y-3 mb-6">
                    {tier.features.map((feature, index) => (
                      <li key={index} className="flex items-start gap-2">
                        <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-600">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <div className="space-y-2">
                    <button
                      onClick={() => handleSelectTier(tier)}
                      className={`w-full flex items-center justify-center gap-2 py-3 rounded-lg font-medium transition-colors ${
                        tier.isPopular
                          ? 'bg-blue-500 text-white hover:bg-blue-600'
                          : 'bg-black text-white hover:bg-gray-800'
                      }`}
                    >
                      <span>Request Information</span>
                      <ArrowRight size={18} />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Application Form */}
          <div id="application-form" className="mb-16 scroll-mt-32">
            <div className="bg-gray-50 rounded-3xl overflow-hidden">
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="p-8 lg:p-12">
                  <h2 className="text-3xl font-bold mb-4">Partner Application</h2>
                  <p className="text-base text-gray-600 mb-8">
                    Complete the form below to apply for partnership. Our team will review your application and contact you within 2 business days.
                  </p>
                  
                  {isSubmitted ? (
                    <div className="bg-green-50 text-green-700 p-6 rounded-xl flex items-start gap-3">
                      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="flex-shrink-0 mt-0.5">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      <div>
                        <h3 className="font-bold text-lg mb-1">Application Submitted Successfully!</h3>
                        <p>Thank you for your interest in partnering with PrivateCharterX. Our partnership team will review your application and contact you within 2 business days.</p>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-6">
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="companyName" className="block text-sm font-medium text-gray-700 mb-1">
                            Company Name
                          </label>
                          <input
                            type="text"
                            id="companyName"
                            name="companyName"
                            value={formData.companyName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="contactName" className="block text-sm font-medium text-gray-700 mb-1">
                            Contact Person
                          </label>
                          <input
                            type="text"
                            id="contactName"
                            name="contactName"
                            value={formData.contactName}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                      </div>
                      
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <div>
                          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                            Email Address
                          </label>
                          <input
                            type="email"
                            id="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                        
                        <div>
                          <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                            Phone Number
                          </label>
                          <input
                            type="tel"
                            id="phone"
                            name="phone"
                            value={formData.phone}
                            onChange={handleChange}
                            required
                            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          />
                        </div>
                      </div>
                      
                      <div>
                        <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-1">
                          Company Website
                        </label>
                        <input
                          type="url"
                          id="website"
                          name="website"
                          value={formData.website}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="https://"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="businessType" className="block text-sm font-medium text-gray-700 mb-1">
                          Business Type
                        </label>
                        <select
                          id="businessType"
                          name="businessType"
                          value={formData.businessType}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        >
                          <option value="">Select business type</option>
                          <option value="wine-caviar-flowers">Wine, Caviar & Flowers</option>
                          <option value="luxury-auto-rental">Luxury Auto Rental</option>
                          <option value="yachting-sailing">Yachting & Sailing</option>
                          <option value="events">Events</option>
                          <option value="concierge">Concierge</option>
                          <option value="airport-services">Airport Services</option>
                          <option value="adventures-sports">Adventures & Sports</option>
                          <option value="helicopter-rundfluge">Helicopter Tours</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="selectedTier" className="block text-sm font-medium text-gray-700 mb-1">
                          Preferred Membership Tier
                        </label>
                        <select
                          id="selectedTier"
                          name="selectedTier"
                          value={formData.selectedTier}
                          onChange={handleChange}
                          required
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                        >
                          <option value="">Select membership tier</option>
                          {membershipTiers.map(tier => (
                            <option key={tier.id} value={tier.id}>{tier.name} ({tier.priceDisplay})</option>
                          ))}
                        </select>
                      </div>
                      
                      <div>
                        <label htmlFor="referralCode" className="block text-sm font-medium text-gray-700 mb-1">
                          Referral Code (Optional)
                        </label>
                        <input
                          type="text"
                          id="referralCode"
                          name="referralCode"
                          value={formData.referralCode}
                          onChange={handleChange}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent"
                          placeholder="Enter company name or email of referrer"
                        />
                        {referralDiscount && (
                          <p className="mt-1 text-sm text-green-600 flex items-center gap-1">
                            <Check size={14} />
                            Valid referral code! You'll receive a 10% discount.
                          </p>
                        )}
                      </div>
                      
                      <div>
                        <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                          Additional Information
                        </label>
                        <textarea
                          id="message"
                          name="message"
                          value={formData.message}
                          onChange={handleChange}
                          rows={4}
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-black focus:border-transparent resize-none"
                          placeholder="Please provide any additional information about your company and services..."
                        ></textarea>
                      </div>
                      
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className="w-full bg-black text-white py-3 rounded-lg hover:bg-gray-800 transition-colors font-medium disabled:opacity-70 flex items-center justify-center gap-2"
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                            <span>Submitting...</span>
                          </>
                        ) : (
                          <>
                            <Send size={18} />
                            <span>Submit Application</span>
                          </>
                        )}
                      </button>
                    </form>
                  )}
                </div>
                
                <div className="hidden lg:block relative">
                  <img 
                    src="https://images.unsplash.com/photo-1540962351504-03099e0a754b?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2574&q=80" 
                    alt="Private Jet" 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Partner Categories */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Our Partner Categories</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center mb-4">
                  <Briefcase size={24} className="text-purple-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Luxury Services</h3>
                <p className="text-gray-600 mb-4">
                  Wine suppliers, caviar providers, florists, luxury car rentals, and concierge services that cater to our discerning clientele.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Direct exposure to high-net-worth individuals</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Integrated into our client journey</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mb-4">
                  <Plane size={24} className="text-blue-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Travel & Transportation</h3>
                <p className="text-gray-600 mb-4">
                  Yacht charters, helicopter tours, airport services, and adventure providers that complement our private aviation offerings.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Seamless integration with flight bookings</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Priority placement in travel packages</span>
                  </li>
                </ul>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mb-4">
                  <Users size={24} className="text-green-600" />
                </div>
                <h3 className="text-xl font-bold mb-2">Events & Experiences</h3>
                <p className="text-gray-600 mb-4">
                  Event organizers, exclusive venue providers, and experience creators that offer unique opportunities for our clients.
                </p>
                <ul className="space-y-2">
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Access to VIP client events</span>
                  </li>
                  <li className="flex items-start gap-2">
                    <Check size={18} className="text-green-500 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-600">Featured in our experience catalog</span>
                  </li>
                </ul>
              </div>
            </div>
          </div>

          {/* Testimonials */}
          <div className="mb-16">
            <h2 className="text-3xl font-bold mb-8 text-center">Partner Testimonials</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-200 rounded-full overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80" 
                        alt="Partner" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold">Michael Reynolds</h4>
                    <p className="text-sm text-gray-500">Luxury Wines & Spirits</p>
                  </div>
                </div>
                <p className="text-gray-600 italic">
                  "Partnering with PrivateCharterX has significantly increased our high-end wine sales. Their clientele appreciates quality and our partnership has been mutually beneficial from day one."
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-200 rounded-full overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1494790108377-be9c29b29330?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=987&q=80" 
                        alt="Partner" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold">Sophia Chen</h4>
                    <p className="text-sm text-gray-500">Luxury Yacht Charters</p>
                  </div>
                </div>
                <p className="text-gray-600 italic">
                  "The Premium partnership has been transformative for our business. The CRM-Ad-Pushing and priority placement have delivered an excellent ROI. Highly recommended."
                </p>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-md border border-gray-100">
                <div className="flex items-center mb-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-gray-200 rounded-full overflow-hidden">
                      <img 
                        src="https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2340&q=80" 
                        alt="Partner" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                  </div>
                  <div className="ml-4">
                    <h4 className="font-bold">Thomas Müller</h4>
                    <p className="text-sm text-gray-500">Helicopter Tours</p>
                  </div>
                </div>
                <p className="text-gray-600 italic">
                  "Since joining as a partner, we've seen a 40% increase in bookings from high-value clients. The dedicated account team is responsive and proactive in promoting our services."
                </p>
              </div>
            </div>
          </div>

          {/* Contact Information */}
          <div className="bg-gray-50 p-8 rounded-2xl mb-16">
            <h2 className="text-2xl font-bold mb-6 text-center">Contact Our Partnership Team</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                    <Mail size={24} className="text-blue-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Email Us</h3>
                    <p className="text-sm text-gray-500">Response within 24 hours</p>
                  </div>
                </div>
                <a href="mailto:partners@privatecharterx.com" className="text-blue-600 hover:underline">
                  partners@privatecharterx.com
                </a>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center">
                    <Phone size={24} className="text-green-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Call Us</h3>
                    <p className="text-sm text-gray-500">Mon-Fri, 9am-6pm CET</p>
                  </div>
                </div>
                <a href="tel:+41447978853" className="text-green-600 hover:underline">
                  +41 44 797 88 53
                </a>
              </div>
              
              <div className="bg-white p-6 rounded-xl shadow-sm">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-purple-100 rounded-full flex items-center justify-center">
                    <Users size={24} className="text-purple-600" />
                  </div>
                  <div>
                    <h3 className="font-bold">Schedule a Meeting</h3>
                    <p className="text-sm text-gray-500">Virtual or in-person</p>
                  </div>
                </div>
                <a href="#application-form" className="text-purple-600 hover:underline">
                  Request via application form
                </a>
              </div>
            </div>
          </div>

          {/* CTA Section */}
          <div className="bg-black text-white p-12 rounded-2xl text-center">
            <h2 className="text-3xl font-bold mb-4">Ready to Grow Your Business?</h2>
            <p className="text-xl mb-8 max-w-2xl mx-auto">
              Join our partner network today and take your business to new heights.
            </p>
            <a 
              href="#application-form" 
              className="inline-flex items-center gap-2 bg-white text-black px-8 py-3 rounded-lg font-medium hover:bg-gray-100 transition-colors"
            >
              Apply Now <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}